import React, { useState } from 'react';
import { Filter, ArrowRight, Plus, X, Pencil, ChevronDown, ChevronRight, Layers } from 'lucide-react';
import { Button } from '@/components/ui/Button/Button';
import { Badge } from '@/components/ui/Badge/Badge';
import { Card, CardContent } from '@/components/ui/Card/Card';
import { SimpleTooltip } from '@/components/ui/Tooltip';
import { AIIcon } from '@/components/ui';
import { cn } from '@/lib/utils';
import { FilterSummaryBadge } from './FilterSummaryBadge';
import type { DestinationWithCount, SearchCriteria } from '@/services/ResearchService';

interface DestinationWithResults extends DestinationWithCount {
  result_count?: number;
}

interface ResearchControlPanelProps {
  // Destinations
  destinations: DestinationWithResults[];
  activeDestinationId: string | null;
  onDestinationChange: (destinationId: string) => void;
  onAddDestination: () => void;
  onRemoveDestination: (destinationId: string) => void;
  onEditDestination: (destinationId: string) => void;
  onEditRegion?: (destination: DestinationWithCount) => void;

  // Search & Filters
  extractedCriteria: SearchCriteria | null;
  onOpenFilters: () => void;
  onRemoveFilter: (filterKey: string) => void;
  isSearching?: boolean;

  // Continue actions
  shortlistCount: number;
  onContinueToRFPs?: () => void;

  // AI Research
  onResearchMore?: () => void;
  hasActiveResearch?: boolean;
}

export function ResearchControlPanel({
  destinations,
  activeDestinationId,
  onDestinationChange,
  onAddDestination,
  onRemoveDestination,
  onEditDestination,
  onEditRegion,
  extractedCriteria,
  onOpenFilters,
  onRemoveFilter,
  isSearching = false,
  shortlistCount = 0,
  onContinueToRFPs,
  onResearchMore,
  hasActiveResearch = false,
}: ResearchControlPanelProps) {
  const hasDestinations = destinations.length > 0;
  const hasShortlist = shortlistCount > 0;
  const [expandedRegions, setExpandedRegions] = useState<Set<string>>(new Set());

  // Count active filters
  const activeFilterCount = extractedCriteria ? Object.values(extractedCriteria).filter(v => v !== null && v !== undefined).length : 0;
  const hasActiveFilters = activeFilterCount > 0;

  function toggleRegion(regionId: string) {
    setExpandedRegions(prev => {
      const next = new Set(prev);
      if (next.has(regionId)) {
        next.delete(regionId);
      } else {
        next.add(regionId);
      }
      return next;
    });
  }

  return (
    <Card className="border-0 rounded-none shadow-sm">
      <CardContent className="py-0 md:py-0 space-y-5">
        {/* Row 1: Filters (left) | Research More + Continue to RFPs (right) */}
        <div className="flex items-center justify-between gap-4">
          {/* Left: Filters */}
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Button
              variant="outline"
              size="sm"
              leftIcon={<Filter className="w-4 h-4" />}
              onClick={onOpenFilters}
              className={cn(
                "flex-shrink-0",
                hasActiveFilters && "border-primary-500 bg-primary-50 hover:bg-primary-100"
              )}
            >
              <span className={cn(hasActiveFilters && "font-medium")}>
                Filters
              </span>
              {hasActiveFilters && (
                <Badge
                  variant="primary"
                  className="ml-2 px-1.5 py-0 text-xs h-5 min-w-[20px] flex items-center justify-center"
                >
                  {activeFilterCount}
                </Badge>
              )}
            </Button>

            {/* Active Filters Inline */}
            {hasActiveFilters && extractedCriteria && (
              <div className="flex-1 min-w-0 overflow-hidden">
                <FilterSummaryBadge
                  criteria={extractedCriteria}
                  onRemoveFilter={onRemoveFilter}
                  compact={true}
                  maxVisible={5}
                />
              </div>
            )}

            {/* Search Status Indicator */}
            {isSearching && (
              <Badge variant="secondary" className="animate-pulse flex-shrink-0">
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 bg-primary-500 rounded-full animate-bounce" />
                  <span className="text-xs">Searching...</span>
                </div>
              </Badge>
            )}
          </div>

          {/* Right: Research More + Continue to RFPs */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Research More Button */}
            {onResearchMore && hasDestinations && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                leftIcon={<AIIcon className="w-4 h-4" />}
                onClick={onResearchMore}
                className={cn(
                  hasActiveResearch && "animate-pulse border-info-500"
                )}
              >
                <span className="hidden sm:inline">Research More</span>
                <span className="sm:hidden">AI</span>
              </Button>
            )}

            {/* Continue to RFPs */}
            <SimpleTooltip
              content={
                !hasShortlist
                  ? "Add venues to your shortlist to continue"
                  : `Continue with ${shortlistCount} shortlisted venue${shortlistCount > 1 ? 's' : ''}`
              }
            >
              <span>
                <Button
                  variant={hasShortlist ? "primary" : "outline"}
                  size="sm"
                  rightIcon={<ArrowRight className="w-4 h-4" />}
                  onClick={onContinueToRFPs}
                  disabled={!hasShortlist || !onContinueToRFPs}
                  className={cn(
                    "relative transition-all",
                    hasShortlist && "shadow-sm hover:shadow-md"
                  )}
                >
                  <span className="hidden sm:inline">Continue to RFPs</span>
                  <span className="sm:hidden">RFPs</span>
                  {hasShortlist && (
                    <Badge
                      variant="primary-inverted"
                      className="ml-2 px-1.5 py-0 text-xs h-5 min-w-[20px] flex items-center justify-center"
                    >
                      {shortlistCount}
                    </Badge>
                  )}
                </Button>
              </span>
            </SimpleTooltip>

          </div>
        </div>

        {/* Row 2: Destination Chips + Add Destination */}
        <div className="flex items-start gap-2 flex-wrap">
          {destinations.map((dest) => {
            const isRegion = dest.destination_type === 'region';
            const children = (dest.children || []) as DestinationWithResults[];
            const isExpanded = expandedRegions.has(dest.id);

            if (isRegion) {
              return (
                <div key={dest.id} className="flex flex-col gap-1">
                  <RegionChip
                    dest={dest}
                    isActive={activeDestinationId === dest.id}
                    isExpanded={isExpanded}
                    childCount={children.length}
                    onSelect={() => onDestinationChange(dest.id)}
                    onToggleExpand={() => toggleRegion(dest.id)}
                    onEdit={onEditRegion ? () => onEditRegion(dest) : undefined}
                    onRemove={() => onRemoveDestination(dest.id)}
                  />
                  {/* Expanded children */}
                  {isExpanded && children.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1.5 ml-4 pl-2 border-l-2 border-primary-200">
                      {children.map((child) => (
                        <DestinationChip
                          key={child.id}
                          dest={child}
                          isActive={activeDestinationId === child.id}
                          isChild={true}
                          onSelect={() => onDestinationChange(child.id)}
                          onEdit={() => onEditDestination(child.id)}
                          onRemove={() => onRemoveDestination(child.id)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <DestinationChip
                key={dest.id}
                dest={dest}
                isActive={activeDestinationId === dest.id}
                isChild={false}
                onSelect={() => onDestinationChange(dest.id)}
                onEdit={() => onEditDestination(dest.id)}
                onRemove={() => onRemoveDestination(dest.id)}
              />
            );
          })}

          {/* Add Destination Button - right after destination chips */}
          <Button
            type="button"
            variant="dashed"
            size="sm"
            leftIcon={<Plus className="w-4 h-4" />}
            onClick={onAddDestination}
          >
            <span className="hidden sm:inline">Add Destination</span>
            <span className="sm:hidden">Add</span>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/** Region destination chip with expand/collapse */
function RegionChip({
  dest,
  isActive,
  isExpanded,
  childCount,
  onSelect,
  onToggleExpand,
  onEdit,
  onRemove,
}: {
  dest: DestinationWithResults;
  isActive: boolean;
  isExpanded: boolean;
  childCount: number;
  onSelect: () => void;
  onToggleExpand: () => void;
  onEdit?: () => void;
  onRemove: () => void;
}) {
  const hasVenues = dest.shortlist_count > 0;
  const hasResults = dest.result_count !== undefined && dest.result_count > 0;
  const displayLabel = dest.display_name || dest.city;

  return (
    <div
      className={cn(
        "group relative inline-flex items-center justify-center font-medium transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        "disabled:pointer-events-none disabled:opacity-50 select-none whitespace-nowrap",
        "h-8 px-3 text-sm rounded-md",
        "border border-input bg-background hover:bg-gray-50 hover:text-foreground",
        isActive && "bg-primary-50 border-primary-500 text-primary-700 hover:bg-primary-100 border-2",
        "cursor-pointer"
      )}
      onClick={onSelect}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      {/* Expand/collapse */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onToggleExpand();
        }}
        className="p-0.5 hover:bg-gray-200 rounded mr-1"
        aria-label={isExpanded ? 'Collapse region' : 'Expand region'}
      >
        {isExpanded ? (
          <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
        ) : (
          <ChevronRight className="w-3.5 h-3.5 text-gray-500" />
        )}
      </button>

      <Layers className="w-3.5 h-3.5 text-primary-500 mr-1 flex-shrink-0" />

      <span className="whitespace-nowrap">{displayLabel}</span>

      {/* City count */}
      <Badge variant="secondary" className="text-xs px-1.5 py-0.5 flex-shrink-0 ml-1.5">
        {childCount} {childCount === 1 ? 'city' : 'cities'}
      </Badge>

      {/* Result Count */}
      {hasResults && (
        <Badge variant="secondary" className="text-xs px-1.5 py-0.5 flex-shrink-0 ml-1">
          {dest.result_count}
        </Badge>
      )}

      {/* Shortlist Count */}
      {hasVenues && (
        <Badge variant="success" className="text-xs px-1.5 py-0.5 flex-shrink-0 ml-1">
          {dest.shortlist_count}
        </Badge>
      )}

      {/* Action Buttons */}
      <div className="opacity-0 group-hover:opacity-100 transition-opacity ml-1.5 flex items-center gap-0.5 flex-shrink-0">
        {onEdit && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            className="p-0.5 hover:bg-gray-200 rounded"
            aria-label={`Edit ${displayLabel}`}
          >
            <Pencil className="w-3.5 h-3.5 text-gray-600" />
          </button>
        )}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="p-0.5 hover:bg-gray-200 rounded"
          aria-label={`Remove ${displayLabel}`}
        >
          <X className="w-3.5 h-3.5 text-gray-600" />
        </button>
      </div>
    </div>
  );
}

/** Standard city/country destination chip */
function DestinationChip({
  dest,
  isActive,
  isChild,
  onSelect,
  onEdit,
  onRemove,
}: {
  dest: DestinationWithResults;
  isActive: boolean;
  isChild: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const hasVenues = dest.shortlist_count > 0;
  const hasResults = dest.result_count !== undefined && dest.result_count > 0;

  return (
    <div
      className={cn(
        "group relative inline-flex items-center justify-center font-medium transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        "disabled:pointer-events-none disabled:opacity-50 select-none whitespace-nowrap",
        "h-8 px-3 text-sm rounded-md",
        "border border-input bg-background hover:bg-gray-50 hover:text-foreground",
        isActive && "bg-primary-50 border-primary-500 text-primary-700 hover:bg-primary-100 border-2",
        "cursor-pointer",
        isChild && "h-7 text-xs"
      )}
      onClick={onSelect}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      {/* Location Name */}
      <span className="whitespace-nowrap">
        {dest.display_name
          ? (isChild ? dest.display_name : `${dest.display_name}, ${dest.country}`)
          : dest.city === dest.country
            ? dest.country
            : isChild
              ? dest.city
              : `${dest.city}, ${dest.country}`
        }
      </span>

      {/* Result Count */}
      {hasResults && (
        <Badge variant="secondary" className="text-xs px-1.5 py-0.5 flex-shrink-0 ml-1.5">
          {dest.result_count}
        </Badge>
      )}

      {/* Shortlist Count */}
      {hasVenues && (
        <Badge variant="success" className="text-xs px-1.5 py-0.5 flex-shrink-0 ml-1">
          {dest.shortlist_count}
        </Badge>
      )}

      {/* Action Buttons */}
      <div className="opacity-0 group-hover:opacity-100 transition-opacity ml-1.5 flex items-center gap-0.5 flex-shrink-0">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
          className="p-0.5 hover:bg-gray-200 rounded"
          aria-label={`Edit ${dest.city === dest.country ? dest.country : `${dest.city}, ${dest.country}`}`}
        >
          <Pencil className="w-3.5 h-3.5 text-gray-600" />
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="p-0.5 hover:bg-gray-200 rounded"
          aria-label={`Remove ${dest.city === dest.country ? dest.country : `${dest.city}, ${dest.country}`}`}
        >
          <X className="w-3.5 h-3.5 text-gray-600" />
        </button>
      </div>
    </div>
  );
}
